import React, { useEffect, useState } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import "react-datepicker/dist/react-datepicker.css";

/* ================= DEFAULT ================= */
const createEmptyParent = () => ({
  relation: "",
  name: "",
  surname: "",
  dateOfBirth: null, // Date object ke liye null set kiya
  dateOfDeath: null,
  placeOfBirth: "",
  currentAddress: "",
  isInCanada: false,
  canadaAddress: ""
});

const ParentForm = () => {
  const [parents, setParents] = useState([createEmptyParent()]);
  const [status, setStatus] = useState("pending");
  const [mode, setMode] = useState("edit");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchParents();
  }, []);

  const fetchParents = async () => {
    try {
      const res = await makeRequestAuth("get", "/user/getParent");
      if (res?.data?.parents?.length) {
        // Backend se aane wali date string ko Date object mein convert karna zaroori hai DatePicker ke liye
        const formattedParents = res.data.parents.map(p => ({
          ...p,
          dateOfBirth: p.dateOfBirth ? new Date(p.dateOfBirth) : null,
          dateOfDeath: p.dateOfDeath ? new Date(p.dateOfDeath) : null
        }));
        setParents(formattedParents);
        setStatus(res.data.status);
        setMode("view");
      } else {
        setParents([createEmptyParent()]);
        setMode("edit");
      }
    } catch {
      setParents([createEmptyParent()]);
      setMode("edit");
    }
  };

  const resetParentsForEdit = () => {
    setParents([createEmptyParent()]);
  };

  const updateParent = (index, field, value) => {
    setParents(prev =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
  };

  const addParent = () => {
    if (parents.length >= 2) return showErrorToast("Only father & mother allowed");
    setParents(prev => [...prev, createEmptyParent()]);
  };

  const removeParent = (index) => {
    setParents(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await makeRequestAuth("post", "/user/createOrUpdateParent", { parents });
      // Refresh logic after save
      fetchParents();
      showSuccessToast("Parents saved successfully ✅");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Pending" },
    approve: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Approved" },
    reject: { color: "bg-rose-100 text-rose-700 border-rose-200", label: "Rejected" }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 antialiased">
      <ToastContainer />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-6 border-b">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Family Details</h1>
          <p className="text-slate-500 text-sm">Submit your parents' information for review</p>
        </div>
        {mode === "view" && (
          <div className={`px-4 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider ${STATUS_CONFIG[status].color}`}>
            • {STATUS_CONFIG[status].label}
          </div>
        )}
      </div>

      {mode === "view" ? (
        <div className="space-y-6 animate-in fade-in duration-500">
          {parents.map((p, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                <h3 className="font-bold text-slate-700 uppercase text-xs tracking-widest">{p.relation || "Parent"} Details</h3>
              </div>
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Preview label="Full Name" value={`${p.name} ${p.surname}`} />
                <Preview label="Date of Birth" value={formatDate(p.dateOfBirth)} />
                <Preview label="Date of Death" value={formatDate(p.dateOfDeath)} />
                <Preview label="Place of Birth" value={p.placeOfBirth} />
                <Preview label="In Canada" value={p.isInCanada ? "Yes" : "No"} />
                <div className="sm:col-span-2 lg:col-span-3">
                  <Preview label="Current Address" value={p.currentAddress} />
                </div>
                {p.isInCanada && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <Preview label="Canada Address" value={p.canadaAddress} />
                  </div>
                )}
              </div>
            </div>
          ))}

          {status === "reject" && (
            <div className="flex justify-center pt-4">
              <button
                onClick={() => { resetParentsForEdit(); setMode("edit"); }}
                className="bg-slate-900 text-white px-10 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-all shadow-lg active:scale-95"
              >
                Edit & Re-submit
              </button>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
          {parents.map((p, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm relative">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs flex items-center justify-center font-black">{i + 1}</span>
                  Parent Information
                </h3>
                {parents.length > 1 && (
                  <button type="button" onClick={() => removeParent(i)} className="text-rose-500 hover:text-rose-600 text-sm font-medium transition-colors">
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Relation</label>
                  <select
                    value={p.relation}
                    onChange={(e) => updateParent(i, "relation", e.target.value)}
                    required
                    className="w-full border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 outline-none transition-all bg-white"
                  >
                    <option value="">Select Relation</option>
                    <option value="father">Father</option>
                    <option value="mother">Mother</option>
                  </select>
                </div>

                <Input label="First Name" value={p.name} onChange={(v) => updateParent(i, "name", v)} />
                <Input label="Surname" value={p.surname} onChange={(v) => updateParent(i, "surname", v)} />

                {/* DATE OF BIRTH (Standard) */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Date of Birth</label>
                  <input
                    type="date"
                    // Convert Date object to string (YYYY-MM-DD) for the native input
                    value={p.dateOfBirth ? new Date(p.dateOfBirth).toISOString().split('T')[0] : ''}
                    onChange={(e) => updateParent(i, "dateOfBirth", e.target.value)}
                    max={new Date().toISOString().split('T')[0]} // Cannot be in the future
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 outline-none transition-all"
                    required
                  />
                </div>

                {/* DATE OF DEATH (Standard) */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Date of Death (If applicable)</label>
                  <input
                    type="date"
                    value={p.dateOfDeath ? new Date(p.dateOfDeath).toISOString().split('T')[0] : ''}
                    onChange={(e) => updateParent(i, "dateOfDeath", e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 outline-none transition-all"
                  />
                </div>

                <Input label="Place of Birth" value={p.placeOfBirth} onChange={(v) => updateParent(i, "placeOfBirth", v)} />

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Current Address</label>
                  <textarea
                    rows="2"
                    value={p.currentAddress}
                    onChange={(e) => updateParent(i, "currentAddress", e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 outline-none transition-all"
                  />
                </div>

                <div className="md:col-span-2 p-4 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                      checked={p.isInCanada}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateParent(i, "isInCanada", checked);
                        if (!checked) updateParent(i, "canadaAddress", "");
                      }}
                    />
                    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">Currently residing in Canada?</span>
                  </label>

                  {p.isInCanada && (
                    <div className="mt-4 animate-in slide-in-from-top-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Canada Address</label>
                      <input
                        value={p.canadaAddress}
                        onChange={(e) => updateParent(i, "canadaAddress", e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 outline-none transition-all"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 pb-10">
            <button
              type="button"
              onClick={addParent}
              className="w-full sm:w-auto px-6 py-2.5 rounded-lg border-2 border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
            >
              <span className="text-lg">+</span> Add Another Parent
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto bg-[#132b45] hover:bg-[#1a3a5c] text-white px-12 py-3.5 rounded-lg font-bold shadow-lg transition-all active:scale-95 disabled:opacity-70"
            >
              {loading ? "Saving..." : "Save Information"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text" }) => (
  <div className="w-full">
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 outline-none transition-all"
    />
  </div>
);

const Preview = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</span>
    <span className="text-slate-800 font-semibold">{value || "-"}</span>
  </div>
);

export default ParentForm;